import React, { useState, useEffect, useCallback } from 'react';
import { maskAIModel } from '../utils/aiBrand';
import { Grid3x3, Save, RefreshCw, Target, Globe, Shield, Zap, CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';

// Use empty string to make relative requests that go through the proxy
const API_BASE = process.env.REACT_APP_API_URL || '';

const AttackMatrixConfig = () => {
  const [config, setConfig] = useState(null);
  const [techniquesMetadata, setTechniquesMetadata] = useState({});
  const [strategies, setStrategies] = useState({});
  const [ollamaModels, setOllamaModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [newDomain, setNewDomain] = useState('');

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
  });

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE}/api/v1/system/attack-matrix`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        setConfig(data.config || {});
        setTechniquesMetadata(data.techniques_metadata || {});
        setStrategies(data.strategies || {});
        setOllamaModels(data.available_ollama_models || []);
      }
    } catch (err) {
      console.error('Attack matrix fetch error:', err);
      setError(`Erreur de connexion: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    setValidationWarnings([]);

    try {
      // First validate
      const validateResponse = await fetch(`${API_BASE}/api/v1/system/attack-matrix/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ config }),
      });

      const validateData = await validateResponse.json();

      if (validateData.warnings && validateData.warnings.length > 0) {
        setValidationWarnings(validateData.warnings);
      }

      if (!validateData.valid && validateData.errors && validateData.errors.length > 0) {
        setError(validateData.errors.join(', '));
        setSaving(false);
        return;
      }

      // Then save - wrap config in object as backend expects { config: {...} }
      const response = await fetch(`${API_BASE}/api/v1/system/attack-matrix`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ config }),
      });

      // Clone response before consuming body to avoid "Body has already been consumed" error
      const responseClone = response.clone();

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = await responseClone.json();
          // Handle different error formats
          if (errorData.detail) {
            if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            } else if (Array.isArray(errorData.detail)) {
              // Pydantic validation errors
              errorMessage = errorData.detail.map(err => {
                const field = err.loc ? err.loc.join('.') : 'unknown';
                return `${field}: ${err.msg}`;
              }).join(', ');
            } else if (typeof errorData.detail === 'object') {
              errorMessage = JSON.stringify(errorData.detail);
            }
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // If JSON parsing fails, use the text
          try {
            errorMessage = await response.text();
          } catch (textError) {
            // Keep default error message
          }
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.success) {
        setSaveMessage('Configuration sauvegardée avec succès');
        // Update config with server response (in case server modified anything)
        if (data.config) {
          setConfig(data.config);
        }
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        throw new Error(data.error || 'Erreur inconnue');
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(`Erreur de sauvegarde: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const addDomain = () => {
    if (newDomain && config && !config.domain?.target_domains?.includes(newDomain)) {
      setConfig({
        ...config,
        domain: {
          ...config.domain,
          target_domains: [...(config.domain?.target_domains || []), newDomain]
        }
      });
      setNewDomain('');
    }
  };

  const removeDomain = (domain) => {
    if (!config) return;
    setConfig({
      ...config,
      domain: {
        ...config.domain,
        target_domains: config.domain?.target_domains?.filter(d => d !== domain) || []
      }
    });
  };

  const toggleTechnique = (technique) => {
    if (!config) return;
    setConfig({
      ...config,
      techniques: {
        ...config.techniques,
        [technique]: !config.techniques?.[technique]
      }
    });
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'low': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'high': return 'text-orange-400';
      case 'critical': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getRiskLevelBg = (level) => {
    switch (level) {
      case 'low': return 'bg-green-500/10 border-green-500/30';
      case 'medium': return 'bg-yellow-500/10 border-yellow-500/30';
      case 'high': return 'bg-orange-500/10 border-orange-500/30';
      case 'critical': return 'bg-red-500/10 border-red-500/30';
      default: return 'bg-gray-500/10 border-gray-500/30';
    }
  };

  // Fetch Ollama models independently for fresh data
  const fetchOllamaModelsList = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/llm/ollama-models`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.models) {
          setOllamaModels(data.models);
        }
      }
    } catch (err) {
      console.error('Error fetching Ollama models:', err);
    }
  }, []);

  useEffect(() => {
    fetchOllamaModelsList();
  }, [fetchOllamaModelsList]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-brand-green animate-spin" />
        <span className="ml-3 text-gray-400">Chargement de la configuration...</span>
      </div>
    );
  }

  // Error state (no config loaded)
  if (error && !config) {
    return (
      <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div>
          <div className="text-red-400 font-medium">Erreur de chargement</div>
          <div className="text-sm text-gray-400">{error}</div>
        </div>
        <button
          onClick={fetchConfig}
          className="ml-auto px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Reessayer
        </button>
      </div>
    );
  }

  if (!config) return null;

  // Use Ollama models directly for selection
  const modelOptions = ollamaModels.length > 0 ? ollamaModels : (config.available_models || []);

  return (
    <div className="space-y-6">
      {/* Header with Save Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Grid3x3 className="w-8 h-8 text-brand-green" />
          <div>
            <h3 className="text-xl font-bold text-white">Matrice des Attaques LLM</h3>
            <p className="text-sm text-gray-400">Configuration IA pour génération automatique d'attaques ciblées</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchConfig}
            disabled={loading}
            className="p-3 bg-brand-black border border-gray-700 rounded-lg hover:border-brand-green/50 transition-colors"
            title="Actualiser"
          >
            <RefreshCw className={`w-5 h-5 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-brand-green to-brand-green-dark text-brand-black font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-green/30 transition-all disabled:opacity-50"
          >
            {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {saveMessage && (
        <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-green-400">{saveMessage}</span>
        </div>
      )}

      {error && config && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-gray-400 hover:text-white">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {validationWarnings.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-yellow-400" />
            <span className="text-yellow-400 font-medium">Avertissements de configuration</span>
          </div>
          <ul className="list-disc list-inside text-sm text-gray-400">
            {validationWarnings.map((warning, idx) => (
              <li key={idx}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      {/* LLM Configuration */}
      <div className="bg-brand-black rounded-xl border border-brand-green/30 p-6">
        <h4 className="text-lg font-semibold text-brand-green mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Configuration LLM
        </h4>

        {/* Ollama Models Info */}
        {ollamaModels.length > 0 && (
          <div className="bg-brand-green/10 border border-brand-green/30 rounded-lg p-3 mb-4">
            <span className="text-brand-green text-sm">
              {ollamaModels.length} modèle(s) SYLink AI disponible(s)
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Modèle LLM</label>
            <select
              value={config.llm_model || ''}
              onChange={(e) => setConfig({...config, llm_model: e.target.value})}
              className="w-full bg-brand-black-lighter border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
            >
              {modelOptions.length === 0 && (
                <option value="">Aucun modèle disponible</option>
              )}
              {modelOptions.map(model => (
                <option key={model.id || model.name} value={model.id || model.name}>
                  {maskAIModel(model.name)} {model.size ? `(${model.size})` : ''} {model.role ? `- ${model.role}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Strategie IA</label>
            <select
              value={config.ai_strategy || 'balanced'}
              onChange={(e) => setConfig({...config, ai_strategy: e.target.value})}
              className="w-full bg-brand-black-lighter border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
            >
              {Object.entries(strategies).map(([key, strategy]) => (
                <option key={key} value={key}>
                  {strategy.name} - {strategy.description}
                </option>
              ))}
            </select>
            {strategies[config.ai_strategy] && (
              <p className={`text-xs mt-1 ${getRiskLevelColor(strategies[config.ai_strategy].risk_level)}`}>
                Risque: {strategies[config.ai_strategy].risk_level}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Temperature: {config.llm_temperature || 0.7}</label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={config.llm_temperature || 0.7}
              onChange={(e) => setConfig({...config, llm_temperature: parseFloat(e.target.value)})}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider-thumb-green"
            />
            <p className="text-xs text-gray-500 mt-1">Creativite de l'IA (0=deterministe, 2=creatif)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Max Tokens</label>
            <input
              type="number"
              value={config.llm_max_tokens || 4096}
              onChange={(e) => setConfig({...config, llm_max_tokens: parseInt(e.target.value)})}
              className="w-full bg-brand-black-lighter border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Context Window</label>
            <input
              type="number"
              value={config.llm_context_window || 32000}
              onChange={(e) => setConfig({...config, llm_context_window: parseInt(e.target.value)})}
              className="w-full bg-brand-black-lighter border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
            />
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <label className={`flex items-center gap-2 ${config.safe_mode ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} text-gray-300 hover:text-brand-green transition-colors`}>
              <input
                type="checkbox"
                checked={config.auto_exploit || false}
                onChange={(e) => setConfig({...config, auto_exploit: e.target.checked})}
                className="w-4 h-4 rounded border-gray-700 text-brand-green focus:ring-brand-green disabled:opacity-50"
                disabled={config.safe_mode}
              />
              <span className="text-sm">Auto Exploit</span>
            </label>
            {config.safe_mode && (
              <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-1">
                <AlertCircle className="w-4 h-4 text-yellow-400" />
                <span className="text-xs text-yellow-400">
                  Desactivez Safe Mode (en bas) pour activer Auto Exploit
                </span>
              </div>
            )}
            {!config.safe_mode && config.auto_exploit && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-1">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="text-xs text-red-400">
                  L'IA peut lancer des exploits automatiquement
                </span>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Auto Exploit permet à l'IA de lancer automatiquement des exploits après détection de vulnérabilités.
            Cette option est contrôlée par Safe Mode pour des raisons de sécurité.
          </p>
        </div>
      </div>

      {/* Internal IP Configuration */}
      <div className="bg-brand-black rounded-xl border border-brand-green/30 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-brand-green flex items-center gap-2">
            <Target className="w-5 h-5" />
            Configuration IP Internes
          </h4>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.internal_ip?.enabled || false}
              onChange={(e) => setConfig({
                ...config,
                internal_ip: {...config.internal_ip, enabled: e.target.checked}
              })}
              className="w-5 h-5 rounded border-gray-700 text-brand-green focus:ring-brand-green"
            />
            <span className="text-sm text-gray-400">Active</span>
          </label>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Plages CIDR a scanner</label>
            <div className="space-y-2">
              {(config.internal_ip?.cidr_ranges || []).map((cidr, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={cidr}
                    readOnly
                    className="flex-1 bg-brand-black-lighter border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                  <span className="text-brand-green text-sm">RFC 1918</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">Profondeur de scan</label>
              <select
                value={config.internal_ip?.scan_depth || 'deep'}
                onChange={(e) => setConfig({
                  ...config,
                  internal_ip: {...config.internal_ip, scan_depth: e.target.value}
                })}
                className="w-full bg-brand-black-lighter border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
              >
                <option value="surface">Surface (Ports communs)</option>
                <option value="standard">Standard (Top 1000)</option>
                <option value="deep">Profond (Tous ports)</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer text-gray-300 hover:text-brand-green transition-colors">
                <input
                  type="checkbox"
                  checked={config.internal_ip?.auto_pivot || false}
                  onChange={(e) => setConfig({
                    ...config,
                    internal_ip: {...config.internal_ip, auto_pivot: e.target.checked}
                  })}
                  className="w-4 h-4 rounded border-gray-700 text-brand-green focus:ring-brand-green"
                />
                <span className="text-sm">Auto Pivot apres exploitation</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer text-gray-300 hover:text-brand-green transition-colors">
                <input
                  type="checkbox"
                  checked={config.internal_ip?.lateral_movement || false}
                  onChange={(e) => setConfig({
                    ...config,
                    internal_ip: {...config.internal_ip, lateral_movement: e.target.checked}
                  })}
                  className="w-4 h-4 rounded border-gray-700 text-brand-green focus:ring-brand-green"
                />
                <span className="text-sm">Mouvement lateral automatique</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Domain Configuration */}
      <div className="bg-brand-black rounded-xl border border-brand-green/30 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-lg font-semibold text-brand-green flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Configuration Domaines
              <div className="relative group">
                <Info className="w-4 h-4 text-gray-400 cursor-help" />
                <div className="absolute left-0 top-6 w-72 p-3 bg-brand-black-lighter border border-brand-green/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                  <p className="text-xs text-gray-300">
                    <strong className="text-brand-green">But:</strong> Configure la reconnaissance basée sur les domaines pour étendre la surface d'attaque.
                  </p>
                  <ul className="text-xs text-gray-400 mt-2 space-y-1">
                    <li>• Ajouter des domaines cibles à scanner</li>
                    <li>• Découvrir automatiquement les sous-domaines</li>
                    <li>• Vérifier la configuration email (SPF/DMARC)</li>
                    <li>• Énumérer les enregistrements DNS</li>
                  </ul>
                </div>
              </div>
            </h4>
            <p className="text-xs text-gray-500 mt-1">Reconnaissance et énumération des domaines cibles</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.domain?.enabled || false}
              onChange={(e) => setConfig({
                ...config,
                domain: {...config.domain, enabled: e.target.checked}
              })}
              className="w-5 h-5 rounded border-gray-700 text-brand-green focus:ring-brand-green"
            />
            <span className="text-sm text-gray-400">Active</span>
          </label>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Domaines cibles
              <span className="ml-2 text-xs text-gray-500">(ex: target.com, client.fr)</span>
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addDomain()}
                placeholder="exemple.com"
                className="flex-1 bg-brand-black-lighter border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-brand-green/50"
              />
              <button
                onClick={addDomain}
                className="px-6 py-2 bg-brand-green/20 text-brand-green border border-brand-green/50 rounded-lg hover:bg-brand-green/30 transition-colors"
              >
                Ajouter
              </button>
            </div>

            <div className="space-y-2">
              {(!config.domain?.target_domains || config.domain.target_domains.length === 0) ? (
                <p className="text-sm text-gray-500 italic">Aucun domaine configure</p>
              ) : (
                config.domain.target_domains.map((domain, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-brand-black-lighter rounded-lg px-4 py-2">
                    <span className="text-white">{domain}</span>
                    <button
                      onClick={() => removeDomain(domain)}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Supprimer
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-brand-black-lighter rounded-lg p-3 border border-gray-700 hover:border-brand-green/50 transition-colors">
              <label className="flex items-center gap-2 cursor-pointer text-gray-300 hover:text-brand-green transition-colors">
                <input
                  type="checkbox"
                  checked={config.domain?.subdomain_enum || false}
                  onChange={(e) => setConfig({
                    ...config,
                    domain: {...config.domain, subdomain_enum: e.target.checked}
                  })}
                  className="w-4 h-4 rounded border-gray-700 text-brand-green focus:ring-brand-green"
                />
                <span className="text-sm font-medium">Enum sous-domaines</span>
              </label>
              <p className="text-xs text-gray-500 mt-2 ml-6">
                Decouvre automatiquement les sous-domaines (*.target.com) via DNS, certificats SSL et OSINT
              </p>
            </div>

            <div className="bg-brand-black-lighter rounded-lg p-3 border border-gray-700 hover:border-brand-green/50 transition-colors">
              <label className="flex items-center gap-2 cursor-pointer text-gray-300 hover:text-brand-green transition-colors">
                <input
                  type="checkbox"
                  checked={config.domain?.dns_bruteforce || false}
                  onChange={(e) => setConfig({
                    ...config,
                    domain: {...config.domain, dns_bruteforce: e.target.checked}
                  })}
                  className="w-4 h-4 rounded border-gray-700 text-brand-green focus:ring-brand-green"
                />
                <span className="text-sm font-medium">Bruteforce DNS</span>
              </label>
              <p className="text-xs text-gray-500 mt-2 ml-6">
                Teste des milliers de noms de sous-domaines courants (admin, dev, staging, etc.)
              </p>
            </div>

            <div className="bg-brand-black-lighter rounded-lg p-3 border border-gray-700 hover:border-brand-green/50 transition-colors">
              <label className="flex items-center gap-2 cursor-pointer text-gray-300 hover:text-brand-green transition-colors">
                <input
                  type="checkbox"
                  checked={config.domain?.spf_dmarc_check || false}
                  onChange={(e) => setConfig({
                    ...config,
                    domain: {...config.domain, spf_dmarc_check: e.target.checked}
                  })}
                  className="w-4 h-4 rounded border-gray-700 text-brand-green focus:ring-brand-green"
                />
                <span className="text-sm font-medium">Check SPF/DMARC</span>
              </label>
              <p className="text-xs text-gray-500 mt-2 ml-6">
                Vérifie la sécurité email: SPF, DKIM, DMARC pour détecter les risques de spoofing
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* MITRE ATT&CK Techniques */}
      <div className="bg-brand-black rounded-xl border border-brand-green/30 p-6">
        <h4 className="text-lg font-semibold text-brand-green mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5" />
          Techniques MITRE ATT&CK
        </h4>
        <p className="text-sm text-gray-400 mb-4">Sélectionnez les phases d'attaque que l'IA peut utiliser</p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(techniquesMetadata).length > 0 ? (
            Object.entries(techniquesMetadata).map(([techniqueId, meta]) => {
              const enabled = config.techniques?.[techniqueId] || false;
              return (
                <button
                  key={techniqueId}
                  onClick={() => toggleTechnique(techniqueId)}
                  className={`flex flex-col items-start p-4 rounded-lg border-2 transition-all ${
                    enabled
                      ? `${getRiskLevelBg(meta.risk_level)} border-current`
                      : 'bg-brand-black-lighter border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between w-full mb-1">
                    <span className={`text-sm font-medium ${enabled ? getRiskLevelColor(meta.risk_level) : ''}`}>
                      {meta.name}
                    </span>
                    {enabled ? (
                      <CheckCircle className={`w-5 h-5 ${getRiskLevelColor(meta.risk_level)}`} />
                    ) : (
                      <XCircle className="w-5 h-5 opacity-30" />
                    )}
                  </div>
                  <span className="text-xs text-gray-500">{meta.mitre_id}</span>
                  {meta.tools && meta.tools.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {meta.tools.slice(0, 3).map((tool, i) => (
                        <span key={i} className="text-xs px-1.5 py-0.5 bg-gray-800 rounded text-gray-400">
                          {tool}
                        </span>
                      ))}
                      {meta.tools.length > 3 && (
                        <span className="text-xs text-gray-500">+{meta.tools.length - 3}</span>
                      )}
                    </div>
                  )}
                </button>
              );
            })
          ) : (
            // Fallback to simple technique display if no metadata
            Object.entries(config.techniques || {}).map(([technique, enabled]) => (
              <button
                key={technique}
                onClick={() => toggleTechnique(technique)}
                className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                  enabled
                    ? 'bg-brand-green/10 border-brand-green text-brand-green'
                    : 'bg-brand-black-lighter border-gray-700 text-gray-400 hover:border-gray-600'
                }`}
              >
                <span className="text-sm font-medium capitalize">
                  {technique.replace(/_/g, ' ')}
                </span>
                {enabled ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <XCircle className="w-5 h-5 opacity-30" />
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Safe Mode Warning */}
      <div className={`rounded-lg border p-4 ${
        config.safe_mode
          ? 'bg-green-500/10 border-green-500/30'
          : 'bg-red-500/10 border-red-500/30'
      }`}>
        <div className="flex items-start gap-3">
          <Shield className={`w-6 h-6 ${config.safe_mode ? 'text-green-400' : 'text-red-400'}`} />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h5 className={`font-semibold ${config.safe_mode ? 'text-green-400' : 'text-red-400'}`}>
                Safe Mode {config.safe_mode ? 'ACTIVE' : 'DESACTIVE'}
              </h5>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.safe_mode || false}
                  onChange={(e) => setConfig({...config, safe_mode: e.target.checked})}
                  className="w-5 h-5 rounded border-gray-700"
                />
              </label>
            </div>
            <p className="text-sm text-gray-400">
              {config.safe_mode
                ? "L'IA ne lancera que des scans non intrusifs et demandera confirmation avant exploitation"
                : "L'IA peut lancer des exploits automatiquement. Utiliser uniquement en environnement autorise."}
            </p>
          </div>
        </div>
      </div>

      {/* Last Updated Info */}
      {config.last_updated && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Info className="w-4 h-4" />
          <span>Dernière mise à jour : {new Date(config.last_updated).toLocaleString('fr-FR')}</span>
        </div>
      )}
    </div>
  );
};

export default AttackMatrixConfig;
